/**
 * PermissionBanner — Reusable banner shown when the user lacks the required
 * permission to access a page or feature. Designed to be dropped into any
 * page layout without coupling to specific permission logic.
 */
import { makeStyles, tokens, MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { ShieldLockRegular } from '@fluentui/react-icons';

export interface PermissionBannerProps {
  /** Custom title. Defaults to "Read-only access". */
  title?: string;
  /** Custom message body. */
  message?: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
    gap: tokens.spacingVerticalL,
    flex: 1,
    minHeight: '300px',
  },
  icon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground3,
  },
  banner: {
    maxWidth: '560px',
    width: '100%',
  },
});

export const PermissionBanner = ({
  title = 'Read-only access',
  message = 'You have read-only access to this application. To manage workflows, connections, and settings, contact the application administrator to request appropriate access.',
}: PermissionBannerProps) => {
  const styles = useStyles();

  return (
    <div className={styles.container}>
      <ShieldLockRegular className={styles.icon} aria-hidden="true" />
      <MessageBar intent="warning" layout="multiline" className={styles.banner}>
        <MessageBarBody>
          <MessageBarTitle>{title}</MessageBarTitle>
          {message}
        </MessageBarBody>
      </MessageBar>
    </div>
  );
};
